import { useStore } from '@nanostores/react';
import { FeatureFlagGuard } from '@/components/guards';
import Journeys from '@/pages/journeys';
import NotFoundPage from '@/pages/not-found.page';
import { AccountFeatureFlag } from '@/services/auth/feature-flags';
import { accountStore } from '@/services/auth/store';

/**
 * Serves the `/journeys/goals` subtree (FLO-4772).
 *
 * Goals have no Hydra page — the pages live in the embedded Journeys app — so
 * flag-on this renders the embedded host and behaves exactly as the
 * `/journeys/$` splat did before these routes existed. Flag-off it renders
 * Hydra's own not-found instead: the goals surfaces are being hidden, and a
 * hidden page must not still answer to a bookmark or a hand-typed URL.
 * Masquerading staff are the exception: they need the embedded goal detail
 * pages to inspect conversion rows before the customer rollout flags are on.
 *
 * Gating here rather than through `route-ownership`'s `flag` field on purpose:
 * that field means "Hydra owns this route while the flag is ON" (rollout
 * semantics), which is the opposite of what goals needs, and ownership is
 * advisory metadata — it never renders anything. The not-found has to come
 * from a route component, and `FeatureFlagGuard` subscribes to the account so
 * it re-evaluates when the flags land rather than caching the pre-auth default.
 *
 * `route-ownership` still gets a matching entry (see `unlessFlag` there): with
 * ownership left saying `embedded` while the flag is off, an intra-Journeys
 * client-side navigation into a goals URL would take the embedded-history
 * fast path in `installEmbeddedAppHistorySync`, skip the TanStack route load
 * entirely, and this guard would never run.
 */
export function GoalsRoute() {
  const account = useStore(accountStore);

  if (account?.is_masq === true) {
    return <Journeys />;
  }

  return (
    <FeatureFlagGuard flag={AccountFeatureFlag.ShowGoals} fallback={NotFoundPage}>
      <Journeys />
    </FeatureFlagGuard>
  );
}
